import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import { supabase } from "../../lib/supabase";

type PageState = "loading" | "set-password" | "success" | "invalid";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Supabase fires PASSWORD_RECOVERY when the user lands here via the reset email link
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setPageState("set-password");
      }
    });

    // Also check if there's an existing session with a recovery token in the hash
    supabase.auth.getSession().then(({ data: sessionData }) => {
      if (sessionData.session && pageState === "loading") {
        // Check URL hash for recovery type
        if (window.location.hash.includes("type=recovery")) {
          setPageState("set-password");
        } else if (pageState === "loading") {
          // No recovery token — user landed here directly
          setPageState("invalid");
        }
      } else if (!sessionData.session && pageState === "loading") {
        // Give the onAuthStateChange listener a moment to fire
        setTimeout(() => {
          setPageState((prev) => (prev === "loading" ? "invalid" : prev));
        }, 1500);
      }
    });

    return () => data.subscription.unsubscribe();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Please fill in both fields.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords don\u2019t match.");
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password: newPassword });
    setLoading(false);

    if (err) {
      setError(err.message || "Failed to update password. Please try again.");
    } else {
      setPageState("success");
      setTimeout(() => navigate("/"), 2500);
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <div className="min-h-screen flex flex-col items-center justify-center px-4 pt-14">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <span className="text-4xl">🍎</span>
            <p className="text-white font-semibold mt-2 text-sm tracking-wide">AppleSMP Bible</p>
          </div>

          <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-7">

            {/* Loading */}
            {pageState === "loading" && (
              <div className="flex flex-col items-center gap-3 py-4">
                <i className="ri-loader-4-line animate-spin text-[#ef4444] text-2xl" />
                <p className="text-[#555] text-sm">Verifying reset link...</p>
              </div>
            )}

            {/* Invalid / No token */}
            {pageState === "invalid" && (
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-[#1a1a1a] rounded-full">
                  <i className="ri-error-warning-line text-[#888] text-xl" />
                </div>
                <p className="text-white font-medium mb-2">Link Expired or Invalid</p>
                <p className="text-[#555] text-sm mb-5 leading-relaxed">
                  This reset link has expired or already been used. Request a new one from the login page.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => navigate("/login")}
                    className="w-full flex items-center justify-center gap-1.5 bg-[#ef4444] hover:bg-[#dc2626] text-white text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Back to Login
                  </button>
                  <Link to="/" className="text-[#444] hover:text-[#888] text-xs cursor-pointer transition-colors py-1">
                    Go to Home
                  </Link>
                </div>
              </div>
            )}

            {/* Set new password */}
            {pageState === "set-password" && (
              <>
                <div className="mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] rounded-full mb-3">
                    <i className="ri-lock-password-line text-[#ef4444] text-lg" />
                  </div>
                  <h2 className="text-white font-semibold text-base mb-1">Set New Password</h2>
                  <p className="text-[#555] text-xs leading-relaxed">
                    Choose a strong password you&apos;ll remember.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-[#555] text-xs uppercase tracking-widest block mb-1.5">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNew ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm rounded-md px-3 py-2.5 pr-10 focus:outline-none focus:border-[#ef4444] placeholder-[#444]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew(!showNew)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#444] hover:text-[#888] cursor-pointer"
                      >
                        <i className={showNew ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-[#555] text-xs uppercase tracking-widest block mb-1.5">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirm ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm rounded-md px-3 py-2.5 pr-10 focus:outline-none focus:border-[#ef4444] placeholder-[#444]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#444] hover:text-[#888] cursor-pointer"
                      >
                        <i className={showConfirm ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
                      </button>
                    </div>
                  </div>

                  {newPassword.length > 0 && (
                    <ul className="text-xs space-y-0.5">
                      <li className={newPassword.length >= 6 ? "text-[#22c55e]" : "text-[#555]"}>
                        <i className={`mr-1 ${newPassword.length >= 6 ? "ri-check-line" : "ri-close-line"}`} />
                        At least 6 characters
                      </li>
                      <li className={newPassword === confirmPassword && confirmPassword.length > 0 ? "text-[#22c55e]" : "text-[#555]"}>
                        <i className={`mr-1 ${newPassword === confirmPassword && confirmPassword.length > 0 ? "ri-check-line" : "ri-close-line"}`} />
                        Passwords match
                      </li>
                    </ul>
                  )}

                  {error && (
                    <p className="text-[#ef4444] text-xs bg-[#ef4444]/10 border border-[#ef4444]/20 rounded-md px-3 py-2 leading-relaxed">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#ef4444] hover:bg-[#dc2626] text-white text-sm font-medium py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading && <i className="ri-loader-4-line animate-spin" />}
                    Set New Password
                  </button>
                </form>
              </>
            )}

            {/* Success */}
            {pageState === "success" && (
              <div className="text-center py-4">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-[#22c55e]/10 rounded-full">
                  <i className="ri-check-double-line text-[#22c55e] text-xl" />
                </div>
                <p className="text-white font-medium mb-2">Password Updated!</p>
                <p className="text-[#555] text-sm leading-relaxed">
                  You&apos;re all set. Redirecting you home...
                </p>
              </div>
            )}
          </div>

          {pageState !== "success" && (
            <div className="text-center mt-4">
              <Link to="/" className="text-[#444] hover:text-[#888] text-xs cursor-pointer transition-colors">
                &larr; Back to Home
              </Link>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
